import { CloseOutlined } from "@mui/icons-material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
} from "@mui/material";
import { UnstyledButton } from "Components/Shared/Buttons/UnstyledButton";
import {
  useExportTransactionsMutation,
  type ExportFormat,
} from "Api/Mutations/Transactions/useExportTransactionsMutation";
import {
  filtersToQueryParams,
  type TransactionFilterParams,
} from "Components/Transactions/utils/transactionFilterUtils";
import type { TransactionFilters } from "Components/Transactions/hooks/useTransactionFilterManager";
import { TransactionStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

const DrawerResources = Resources.Components.Transactions.ExportDrawer;

type DownloadOption = {
  format: ExportFormat;
  label: string;
  filters: TransactionFilterParams;
};

type Props = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  filters: TransactionFilters;
};

export const ExportDrawer = ({ isOpen, onOpen, onClose, filters }: Props) => {
  const { t } = useResource();
  const { mutate: exportTransactions } = useExportTransactionsMutation();

  const downloadOptions: Array<DownloadOption> = [
    {
      format: "Pdf",
      label: t(DrawerResources.ExportSelectionPdf),
      filters: filtersToQueryParams(filters),
    },
    {
      format: "Xlsx",
      label: t(DrawerResources.ExportSelectionXlsx),
      filters: filtersToQueryParams(filters),
    },
    {
      format: "Csv",
      label: t(DrawerResources.ExportSelectionCsv),
      filters: filtersToQueryParams(filters),
    },
    {
      format: "Csv",
      label: t(DrawerResources.ExportTodayRevenueCsv),
      filters: {
        ...filtersToQueryParams({ timePeriod: "day" }),
        status: TransactionStatus.Success,
      },
    },
  ];

  const handleDownload = ({
    format,
    filters,
  }: Pick<DownloadOption, "format" | "filters">) => {
    exportTransactions(
      { format, filters },
      { onError: () => toast.error(t(Resources.Common.DownloadError)) },
    );
    onClose();
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      slotProps={{
        paper: {
          sx: {
            p: 3,
            maxHeight: "85vh",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        },
      }}
      disableDiscovery
    >
      <Box display="flex" justifyContent="center" mb={6}>
        <Box
          aria-hidden
          sx={{ width: 80, height: 6, bgcolor: "grey.300", borderRadius: 2 }}
        />
        <UnstyledButton
          aria-label={t(Resources.Common.Back)}
          onClick={onClose}
          sx={{
            position: "absolute",
            color: "grey.600",
            right: 12,
            top: 12,
          }}
        >
          <CloseOutlined />
        </UnstyledButton>
      </Box>
      <List disablePadding sx={{ width: "100%", maxWidth: 600, mx: "auto" }}>
        {downloadOptions.map(({ label, ...rest }) => (
          <ListItemButton key={label} onClick={() => handleDownload(rest)}>
            <ListItemIcon>
              <FileDownloadIcon />
            </ListItemIcon>
            <ListItemText primary={label} />
          </ListItemButton>
        ))}
      </List>
    </SwipeableDrawer>
  );
};
